import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { getLead, listLeadTasks, listLeads, listStaff, listStages, type Lead, type LeadFilters } from "../../api/leads";
import { useServices } from "../../app/services";
import { useHomeScope, type Scope } from "../home/hooks";
import { leads as t } from "./strings";

export type { Scope };
export const useScope = useHomeScope;

export function useStages(sc: Scope) {
  const { client } = useServices();
  return useQuery({ queryKey: ["lead-stages", ...sc.scopeKey], queryFn: () => listStages(client), retry: false, staleTime: 5 * 60_000 });
}

/** Staff names for owner labels and filters. GET /staff needs `staff.view`; without it only "You" can be named. */
export function useStaffNames(sc: Scope) {
  const { client } = useServices();
  const enabled = sc.ctx.has("staff.view");
  const q = useQuery({ queryKey: ["staff", ...sc.scopeKey], queryFn: () => listStaff(client), enabled, retry: false, staleTime: 5 * 60_000 });
  const myId = sc.ctx.me?.staff.id ?? null;
  return useMemo(() => {
    const byId = new Map((q.data ?? []).map((s) => [s.id, s.name]));
    return {
      list: q.data ?? [],
      canList: enabled && !q.isError,
      pending: enabled && q.isPending,
      error: q.error,
      refetch: q.refetch,
      nameOf: (id: string | null | undefined): string | null => (!id ? null : id === myId ? (sc.ctx.me?.staff.name ?? t.you) : (byId.get(id) ?? null)),
    };
  }, [q.data, q.isError, q.isPending, q.error, q.refetch, enabled, myId, sc.ctx.me?.staff.name]);
}

export function useLeadPages(sc: Scope, filters: LeadFilters, enabled = true) {
  const { client } = useServices();
  const f: LeadFilters = { ...filters, ...(sc.branchId ? { branch: sc.branchId } : {}) };
  return useInfiniteQuery({
    queryKey: ["leads", ...sc.scopeKey, f],
    queryFn: ({ pageParam }) => listLeads(client, f, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (p) => p.nextCursor,
    enabled,
    retry: false,
  });
}

export const flatten = (pages: ReadonlyArray<{ leads: Lead[] }> | undefined): Lead[] => pages?.flatMap((p) => p.leads) ?? [];

export function useLead(sc: Scope, id: string) {
  const { client } = useServices();
  return useQuery({ queryKey: ["lead", ...sc.scopeKey, id], queryFn: () => getLead(client, id), retry: false });
}

export function useLeadTasks(sc: Scope, enabled = true) {
  const { client } = useServices();
  return useQuery({ queryKey: ["lead-tasks", ...sc.scopeKey], queryFn: () => listLeadTasks(client), enabled, retry: false });
}

/** Re-read everything a lead write can change. */
export function useInvalidateLeadData() {
  const qc = useQueryClient();
  return () => Promise.all(["leads", "lead", "lead-tasks", "followups", "appointments"].map((k) => qc.invalidateQueries({ queryKey: [k] })));
}
